import { api } from '../api.js';
import { escapeHtml } from '../../../../src/shared/escape-html.js';
import { formatShortDate } from '../../../../src/shared/format-time.js';
import { confirmAdminRoleChange } from '../admin-role-confirmation.js';
import { runSensitiveAdminMutation } from '../admin-security.js';

// ── Users ────────────────────────────────────────────────

let usersBound = false;

// Task 1.7 (spec §2/§3.1) — the ONE deliberate no-rewrite exception. The
// `users` [data-screen] DOM is REUSED across navigations (the router never
// recreates it — see router.js), so the search box's state must be derived
// from the CURRENT URL on every load rather than trusting whatever was
// last typed: `?search=foo` pre-fills it; a load with no `search` param (or
// an explicitly empty one, `?search=`) clears it back out. renderUsers()
// below always reads the input's live value to build its fetch, so setting
// it here is enough to also reset/apply the filter.
function applySearchFromUrl() {
  const searchEl = document.getElementById('admin-users-search');
  if (searchEl) searchEl.value = new URLSearchParams(location.search).get('search') || '';
}

async function renderUsers() {
  const body = document.getElementById('admin-users-body');
  const countEl = document.getElementById('admin-users-count');
  const searchEl = document.getElementById('admin-users-search');
  if (!body) return;

  if (!usersBound) {
    usersBound = true;
    let t = null;
    searchEl?.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => renderUsers(), 250);
    });
  }

  const search = (searchEl?.value || '').trim();
  body.innerHTML = '<tr><td colspan="10" class="admin-loading">Loading users…</td></tr>';

  try {
    const url = search
      ? `/api/admin/users?search=${encodeURIComponent(search)}`
      : '/api/admin/users';
    const users = await api.get(url);

    if (countEl) countEl.textContent = `${users.length.toLocaleString()} user${users.length === 1 ? '' : 's'}`;

    if (!users.length) {
      body.innerHTML = '<tr><td colspan="10" class="admin-loading">No users match.</td></tr>';
      return;
    }

    body.innerHTML = '';
    for (const u of users) {
      const row = document.createElement('tr');
      const unverifiedBadge = u.email_verified
        ? ''
        : '<span class="admin-user-unverified" title="Email not verified">unverified</span>';
      const roleBadges = (u.roles || []).map(r =>
        `<span class="admin-user-role admin-user-role-${escapeHtml(r)}">${escapeHtml(r)}</span>`
      ).join('');
      const PROVIDER_LABELS = { credential: 'password', discord: 'discord', google: 'google' };
      const providerBadges = (u.providers || [])
        .map(p => {
          const label = PROVIDER_LABELS[p] || p;
          return `<span class="admin-user-provider admin-user-provider-${escapeHtml(p)}">${escapeHtml(label)}</span>`;
        })
        .join('') || '<span class="admin-user-role-none">–</span>';
      row.innerHTML =
        `<td class="admin-user-id">${escapeHtml(u.id)}</td>` +
        `<td><a href="/admin/users/${encodeURIComponent(u.id)}" class="admin-user-username">${escapeHtml(u.username)}</a></td>` +
        `<td class="admin-user-name">${u.name ? escapeHtml(u.name) : '<span class="admin-user-role-none">–</span>'}</td>` +
        `<td class="admin-user-email">${escapeHtml(u.email)} ${unverifiedBadge}</td>` +
        `<td>${providerBadges}</td>` +
        `<td>${roleBadges || '<span class="admin-user-role-none">–</span>'}</td>` +
        `<td><label class="admin-toggle"><input type="checkbox" data-user-id="${escapeHtml(u.id)}" data-field="is_test" ${u.is_test ? 'checked' : ''}><span class="admin-toggle-slider"></span></label></td>` +
        `<td class="admin-user-num">${u.session_count.toLocaleString()}</td>` +
        `<td class="admin-user-date">${u.last_played_at ? formatShortDate(new Date(u.last_played_at).getTime()) : '<span class="admin-user-role-none">never</span>'}</td>` +
        `<td class="admin-user-date">${formatShortDate(new Date(u.created_at).getTime())}</td>`;
      body.appendChild(row);
    }
    body.querySelectorAll('input[data-field="is_test"]').forEach(cb => {
      cb.addEventListener('change', async () => {
        const userId = cb.dataset.userId;
        const value = cb.checked;
        try {
          const completed = await runSensitiveAdminMutation({
            actionLabel: `set ${userId}'s test-account status`,
            mutate: (reason) => api.patch(
              `/api/admin/users/${encodeURIComponent(userId)}/test`,
              { is_test: value, reason },
            ),
          });
          if (!completed) cb.checked = !value;
        } catch {
          cb.checked = !value;
        }
      });
    });
  } catch (err) {
    body.innerHTML = `<tr><td colspan="10" class="admin-loading">Error: ${escapeHtml(err.message)}</td></tr>`;
    console.error('[admin] users load error:', err);
  }
}

// ── Roles ────────────────────────────────────────────────

async function grantRole() {
  const input = document.getElementById('admin-grant-username');
  const select = document.getElementById('admin-grant-role');
  const status = document.getElementById('admin-roles-status');
  if (!input || !select) return;

  const username = input.value.trim();
  const role = select.value;
  if (!username) {
    if (status) { status.textContent = 'Enter a username'; status.className = 'admin-status error'; }
    return;
  }

  try {
    const users = await api.get(`/api/admin/users?search=${encodeURIComponent(username)}`);
    const match = users.find(u => u.username.toLowerCase() === username.toLowerCase());
    if (!match) {
      if (status) { status.textContent = `User "${username}" not found`; status.className = 'admin-status error'; }
      setTimeout(() => { if (status) status.textContent = ''; }, 4000);
      return;
    }
    if (!confirmAdminRoleChange({
      operation: 'grant',
      role,
      username: match.username,
    })) return;
    const completed = await runSensitiveAdminMutation({
      actionLabel: `grant ${role} access to ${match.username}`,
      mutate: (reason) => api.post('/api/admin/roles', {
        user_id: match.id,
        role,
        reason,
      }),
    });
    if (!completed) return;
    input.value = '';
    if (status) { status.textContent = `Granted ${role} to ${username}`; status.className = 'admin-status success'; }
    setTimeout(() => { if (status) status.textContent = ''; }, 4000);
    renderRoles();
  } catch (err) {
    if (status) { status.textContent = `Error: ${err.message}`; status.className = 'admin-status error'; }
    setTimeout(() => { if (status) status.textContent = ''; }, 4000);
  }
}

async function renderRoles() {
  const list = document.getElementById('admin-roles-list');
  const status = document.getElementById('admin-roles-status');
  if (!list) return;

  list.innerHTML = '<div class="admin-loading">Loading roles...</div>';

  try {
    const roles = await api.get('/api/admin/roles');

    if (roles.length === 0) {
      list.innerHTML = '<div class="admin-roles-empty">No roles assigned yet. Bootstrap admins are set via ADMIN_USERS env var.</div>';
      return;
    }

    list.innerHTML = '';
    for (const r of roles) {
      const row = document.createElement('div');
      row.className = 'admin-role-row';
      // Every interpolated value is user- or DB-derived — escape it (matching the
      // escaping already used on this row's data-* attributes) so a hostile
      // username/role can only ever render as inert text, never markup.
      const roleUser = escapeHtml(r.username || r.user_id);
      const role = escapeHtml(r.role);
      const grantedBy = escapeHtml(r.granted_by_username || 'env');
      row.innerHTML = `
        <span class="admin-role-user" title="${roleUser}">${roleUser}</span>
        <span class="admin-role-badge admin-role-${role}">${role}</span>
        <span class="admin-role-granted">by ${grantedBy}</span>
        <button class="btn-tiny admin-role-revoke" data-uid="${escapeHtml(r.user_id)}" data-role="${role}" data-username="${roleUser}" title="Revoke">x</button>
      `;
      list.appendChild(row);
    }

    list.querySelectorAll('.admin-role-revoke').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const uid = e.target.dataset.uid;
        const role = e.target.dataset.role;
        const username = e.target.dataset.username || uid;
        if (!confirmAdminRoleChange({ operation: 'revoke', role, username })) return;
        try {
          const completed = await runSensitiveAdminMutation({
            actionLabel: `revoke ${role} access from ${username}`,
            mutate: (reason) => api.delete('/api/admin/roles', {
              user_id: uid,
              role,
              reason,
            }),
          });
          if (!completed) return;
          renderRoles();
        } catch (err) {
          if (status) {
            status.textContent = `Error: ${err.message}`;
            status.className = 'admin-status error';
            setTimeout(() => { status.textContent = ''; }, 4000);
          }
        }
      });
    });
  } catch (err) {
    list.innerHTML = `<div class="admin-loading">Error: ${err.message}</div>`;
  }
}

// ── Render entry point ──────────────────────────────────

let _bound = false;

export function render() {
  if (!_bound) {
    _bound = true;
    document.getElementById('admin-grant-btn')?.addEventListener('click', () => grantRole());
  }

  applySearchFromUrl();
  renderUsers();
  renderRoles();
}
